/**
 * PDF 转长图工具函数
 * 将PDF内容渲染为单张长图，用于图片型PDF识别
 */

import { pdfjs } from 'react-pdf';

// 设置 PDF.js worker
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

// 最大图片大小 8MB
const MAX_IMAGE_SIZE = 8 * 1024 * 1024;

// JPEG 质量（中等）
const JPEG_QUALITY = 0.7;

/**
 * 将PDF文件渲染为单张长图
 * @param pdfFile PDF文件
 * @returns 渲染后的JPEG图片文件
 */
export async function renderPdfToImage(pdfFile: File): Promise<File> {
  // 读取PDF文件
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

  // 计算总高度和最大宽度
  let totalHeight = 0;
  let maxWidth = 0;
  const pageCanvases: HTMLCanvasElement[] = [];

  // 渲染每一页
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.5 }); // 适当缩放保证清晰度

    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('无法创建 canvas 上下文');
    }

    // 渲染页面到 canvas
    await page.render({
      canvasContext: ctx,
      viewport: viewport,
    }).promise;

    pageCanvases.push(canvas);
    totalHeight += viewport.height + 20; // 页面间距20px
    maxWidth = Math.max(maxWidth, viewport.width);
  }

  // 创建长图 canvas
  const longCanvas = document.createElement('canvas');
  longCanvas.width = maxWidth;
  longCanvas.height = totalHeight;

  const longCtx = longCanvas.getContext('2d');
  if (!longCtx) {
    throw new Error('无法创建长图 canvas 上下文');
  }

  // 填充白色背景
  longCtx.fillStyle = '#ffffff';
  longCtx.fillRect(0, 0, maxWidth, totalHeight);

  // 拼接所有页面
  let currentY = 0;
  for (const canvas of pageCanvases) {
    longCtx.drawImage(canvas, 0, currentY);
    currentY += canvas.height + 20; // 添加页面间距
  }

  // 导出为 JPEG，质量 0.7（中等）
  const blob = await new Promise<Blob | null>((resolve) => {
    longCanvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
  });

  if (!blob) {
    throw new Error('图片生成失败');
  }

  // 检查文件大小是否超过 8MB
  if (blob.size > MAX_IMAGE_SIZE) {
    throw new Error(
      'PDF内容过多，生成的图片超过8MB限制，请尝试压缩PDF或减少页数'
    );
  }

  // 生成文件名
  const baseName = pdfFile.name.replace(/\.[^/.]+$/, '');
  const fileName = `${baseName}-rendered.jpg`;

  return new File([blob], fileName, { type: 'image/jpeg' });
}

/**
 * 检查文件是否为PDF
 * @param file 文件
 * @returns 是否为PDF
 */
export function isPdfFile(file: File): boolean {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );
}
